import React, { useEffect, useState } from 'react'
import {
  columnFilteringFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_equalsString,
  filterFn_includesString,
  globalFilteringFeature,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
  type ColumnDef,
  type ColumnFiltersState,
  type Header,
  type RowData,
  type RowSelectionState,
  type SortingState
} from '@tanstack/react-table'
import Button from 'components/button'
import Checkbox from 'components/checkbox'
import Skeleton from 'components/skeleton'
import { Arrow, ExpandAll } from 'components/icons'
import DataTablePagination from 'components/data-table/pagination'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/data-table/table'

// A table of records, in the manner of shadcn/ui's data table: TanStack Table works out which rows show,
// in what order and on which page; the markup and its look are ours (data-table/table). A column is
// sorted by clicking its header (shift+click adds it to the sort), the rows can be searched and filtered,
// they're paged, and they can be ticked. It shows its own loading, empty, no-match and failed states.

export interface DataTableColumnMeta {
  /** Classes for the column's header and cells: a width, or `text-right` for numbers and actions. */
  className?: string
}

const features = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime, text: sortFn_text },
  filterFns: { includesString: filterFn_includesString, equalsString: filterFn_equalsString },
  columnMeta: metaHelper<DataTableColumnMeta>()
})

export type DataTableFeatures = typeof features
export type DataTableColumn<TData extends RowData> = ColumnDef<DataTableFeatures, TData, any>

/** Builds a table's columns: `const columns = col.columns([col.accessor('name', { header: 'Name' })])`. */
export const dataTableColumns = <TData extends RowData>() => createColumnHelper<DataTableFeatures, TData>()

export interface DataTableProps<TData extends RowData> {
  /** What the table lists, for screen readers: "Deleted documents and tables". */
  label: string
  /**
   * Made once, outside the component (see dataTableColumns). Each cell is drawn as a component of its
   * own, so columns made afresh on every render remount every cell, losing focus and anything in it.
   * A cell that shows changing state reads it from a context or a hook, not from the columns.
   */
  columns: DataTableColumn<TData>[]
  /** The rows; undefined while they're loading. */
  data: TData[] | undefined
  getRowId: (row: TData) => string
  /** What a row is called, to name its tick: "Select Site register" rather than "Select row". */
  rowLabel?: (row: TData) => string
  initialSort?: SortingState
  /** Text to find in the searchable columns: those holding text, unless a column turns it off. */
  search?: string
  filters?: ColumnFiltersState
  /** Columns that sort or filter the rows without being shown. */
  hiddenColumns?: string[]
  /** Rows per page. Paging controls show only when there's more than one page. */
  pageSize?: number
  /** False for every row on one page, as for rows that load more as they're asked for. */
  paged?: boolean
  /** Ticks down the left edge, and one in the header to tick every row on the page. */
  selection?: { selected: RowSelectionState; onChange: (selected: RowSelectionState) => void }
  /** Shown when there are no rows at all. */
  empty: React.ReactNode
  /** Shown when there are rows, but none match the search and filters. */
  noMatches?: React.ReactNode
  /** Shown in place of the rows when they couldn't be loaded. */
  error?: React.ReactNode
  stickyHeader?: boolean
  /** A line under the table, at its left (the paging, when there is any, at its right): "4 members". */
  summary?: React.ReactNode
  /** Classes for a row of its own, such as a fill marking it out. */
  rowClassName?: (row: TData) => string | undefined
}

const SKELETON_ROWS = 3
// The ticks' column: as narrow as a tick, and tight to the column after it.
const SELECT_CELL = 'w-0 pr-0'
const NO_COLUMNS: string[] = []
const NO_FILTERS: ColumnFiltersState = []
const NO_SELECTION: RowSelectionState = {}

/** The arrow beside a sortable header: which way it's sorted, or a faint up-and-down while it isn't. */
const SortMark: React.FC<{ direction: false | 'asc' | 'desc' }> = ({ direction }) => {
  if (direction === 'asc') return <Arrow type="up" className="size-3.5" />
  if (direction === 'desc') return <Arrow type="down" className="size-3.5" />
  return <ExpandAll className="size-3.5 opacity-50" />
}

const ariaSort = (direction: false | 'asc' | 'desc') => {
  if (direction === 'asc') return 'ascending'
  if (direction === 'desc') return 'descending'
  return undefined
}

const DataTable = <TData extends RowData>({
  label,
  columns,
  data,
  getRowId,
  rowLabel,
  initialSort = [],
  search = '',
  filters,
  hiddenColumns = NO_COLUMNS,
  pageSize: rowsPerPage = 20,
  paged = true,
  selection,
  empty,
  noMatches = 'Nothing matches your search.',
  error,
  stickyHeader,
  summary,
  rowClassName
}: DataTableProps<TData>): React.ReactElement => {
  const [noRows] = useState<TData[]>([])
  const pageSize = paged ? rowsPerPage : Number.MAX_SAFE_INTEGER
  const columnVisibility = Object.fromEntries(hiddenColumns.map((id) => [id, false]))

  const table = useTable({
    features,
    columns,
    data: data ?? noRows,
    getRowId,
    initialState: { sorting: initialSort, pagination: { pageIndex: 0, pageSize } },
    state: {
      globalFilter: search,
      columnFilters: filters ?? NO_FILTERS,
      columnVisibility,
      rowSelection: selection?.selected ?? NO_SELECTION
    },
    onRowSelectionChange: (updater) =>
      selection?.onChange(typeof updater === 'function' ? updater(selection.selected) : updater),
    globalFilterFn: 'includesString',
    enableSortingRemoval: false,
    enableRowSelection: !!selection,
    // Restoring or removing a row shouldn't send you back to the first page; a new search or filter should.
    autoResetPageIndex: false
  })

  const { pageIndex } = table.state.pagination
  const pageCount = table.getPageCount()
  const filterKey = JSON.stringify([search, filters ?? NO_FILTERS])
  useEffect(() => {
    table.setPageIndex(0)
  }, [filterKey])
  // Off the end, after the last rows of the last page went: back to what's now the last page.
  useEffect(() => {
    if (pageCount > 0 && pageIndex > pageCount - 1) table.setPageIndex(pageCount - 1)
  }, [pageIndex, pageCount])

  const visibleColumns = table.getVisibleLeafColumns()
  const columnCount = visibleColumns.length + (selection ? 1 : 0)
  const pageRows = table.getRowModel().rows

  const message = (content: React.ReactNode) => (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columnCount} className="h-32 whitespace-normal text-center text-muted">
        {content}
      </TableCell>
    </TableRow>
  )

  const body = () => {
    if (error) return message(error)
    if (data === undefined)
      return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <TableRow key={index} className="hover:bg-transparent" data-testid="data-table-skeleton">
          {selection && <TableCell className={SELECT_CELL} />}
          {visibleColumns.map((column) => (
            <TableCell key={column.id} className={column.columnDef.meta?.className}>
              <Skeleton className="h-3 w-3/5 rounded" />
            </TableCell>
          ))}
        </TableRow>
      ))
    if (data.length === 0) return message(empty)
    if (pageRows.length === 0) return message(noMatches)
    return pageRows.map((row) => (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() ? 'selected' : undefined}
        className={rowClassName?.(row.original)}
      >
        {selection && (
          <TableCell className={SELECT_CELL}>
            <Checkbox
              label={rowLabel ? `Select ${rowLabel(row.original)}` : 'Select row'}
              className="my-0"
              checked={row.getIsSelected()}
              onChange={(event) => row.toggleSelected(event.target.checked)}
            />
          </TableCell>
        )}
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className={cell.column.columnDef.meta?.className}>
            <table.FlexRender cell={cell} />
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  const head = (header: Header<DataTableFeatures, TData, unknown>) => {
    if (header.isPlaceholder) return null
    if (!header.column.getCanSort()) return <table.FlexRender header={header} />
    return (
      <Button
        internalType="transparent"
        className="-ml-2 h-7 gap-1 px-2 font-medium text-body hover:text-body"
        onClick={header.column.getToggleSortingHandler()}
      >
        <table.FlexRender header={header} />
        <SortMark direction={header.column.getIsSorted()} />
      </Button>
    )
  }

  return (
    <div className="flex flex-col">
      <Table aria-label={label}>
        <TableHeader sticky={stickyHeader}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {selection && (
                <TableHead className={SELECT_CELL}>
                  <Checkbox
                    label="Select all rows on this page"
                    className="my-0"
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={(event) => table.toggleAllPageRowsSelected(event.target.checked)}
                  />
                </TableHead>
              )}
              {group.headers.map((header) => (
                <TableHead
                  key={header.id}
                  aria-sort={ariaSort(header.column.getIsSorted())}
                  className={header.column.columnDef.meta?.className}
                >
                  {head(header)}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{body()}</TableBody>
      </Table>
      {!error && (summary || pageCount > 1) && (
        <div className="flex items-center gap-3 border-t border-divider px-4 py-2.5">
          {summary && <div className="text-[13px] text-muted tabular-nums">{summary}</div>}
          {pageCount > 1 && (
            <DataTablePagination
              pageIndex={pageIndex}
              pageSize={pageSize}
              rowCount={table.getPrePaginatedRowModel().rows.length}
              onPrevious={() => table.previousPage()}
              onNext={() => table.nextPage()}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default DataTable
